import React from "react";
import { v3 } from "oc-threejs/short3";
import { Matrix4, Vector2, Vector3 } from "three";
import { CoordinateSystem } from "./CoordinateSystem";
import { Pfosten } from "../../arrangement/Pfosten";
import { ZElement } from "../../arrangement/ZElement";
import { punkteZuLinie } from "./Zaunlinien";

export function Part({ cs, part }: { cs: CoordinateSystem; part: ZElement | Pfosten }) {
  const strokeWidth = cs.lengthToScreen(part.size.z || 0.04);
  const isSelected = APP.arrangement.selectedObjects.includes(part);
  const className =
    "svg-line-part " +
    (isSelected ? "selected" : "") +
    " " +
    ((part as ZElement).isTor ? "tor" : "");
  const drawLine = (points: Vector2[]) => (
    <line {...punkteZuLinie(points)} strokeWidth={strokeWidth} className={className}></line>
  );
  if (part instanceof ZElement && part.isSchiebetor) {
    return <SchiebetorGrundriss cs={cs} schiebetor={part} className={className} />;
  }
  if (part instanceof ZElement && part.isTor) {
    const gflügel = part.flügel.map((flügel) => {
      const box = flügel.getBox();
      return {
        xs: [box.min.x, box.max.x],
        isAufhängungAnLinkerTorseite: flügel.isAufhängungAnLinkerTorseite,
        isÖffnungInSegmentNormale: flügel.isÖffnungInSegmentNormale,
        localToWorld: (v) => flügel.localToWorld(v),
      };
    });
    return <TorFlügel cs={cs} gflügel={gflügel} className={className} />;
  }
  const box = part.getBox();
  // Schiebetorpfosten haben einen Z-Versatz im 3D-Modell, der hier übernommen wird,
  // damit sie im Grundriss nicht mittig auf der Zaunlinie sondern parallel versetzt
  // wie das Schiebetor selbst gezeichnet werden.
  const zOffset =
    part instanceof Pfosten && part.isSchiebetorpfosten ? part.model?.position.z ?? 0 : 0;
  const endPoints = [v3(box.min.x, 0, zOffset), v3(box.max.x, 0, zOffset)]
    .map((v) => part.localToWorld(v))
    .map(cs.pointToScreen);
  return drawLine(endPoints);
}

/**
 * Zeichnet ein Schiebetor in der Grundrissansicht: einen versetzten horizontalen
 * Balken (das Tor selbst), den Auflaufschuh (Rolle am Endanschlag) und den
 * E-Antrieb-Kasten (Motor neben dem Doppelpfosten). Positionen und Größen werden
 * von den vorhandenen 3D-Modellen übernommen.
 */
function SchiebetorGrundriss({
  cs,
  schiebetor,
  className,
}: {
  cs: CoordinateSystem;
  schiebetor: ZElement;
  className: string;
}) {
  const fixPunkt = schiebetor.schiebetorFixPunkt;
  const torDicke = 0.06;

  // Schiebetor-Balken: vom Einzelpfosten (lokal x=0) bis zum Doppelpfosten (x=breite),
  // dort wo das Tor in geschlossener Position steht.
  const balkenStart = fixPunkt.localToWorld(v3(0, 0, 0));
  const balkenEnd = fixPunkt.localToWorld(v3(schiebetor.breite, 0, 0));
  const dreiecksBreite = schiebetor.schiebetorRahmenDef?.dreiecksBreite || 0;
  const balkenEndDreieck = fixPunkt.localToWorld(v3(schiebetor.breite + dreiecksBreite, 0, 0));
  const balkenEndOffen = fixPunkt.localToWorld(v3(schiebetor.breite * 2 + dreiecksBreite, 0, 0));
  const balkenStartScreen = cs.pointToScreen(balkenStart);
  const balkenEndScreen = cs.pointToScreen(balkenEnd);
  const balkenEndDreieckScreen = cs.pointToScreen(balkenEndDreieck);
  const balkenEndOffenScreen = cs.pointToScreen(balkenEndOffen);
  const balkenStrokeWidth = cs.lengthToScreen(torDicke);

  // Modell-Boxen als Polygone übertragen (jeweils im lokalen Frame des Modells)
  const renderModelBox = (modelKey: string, key: string) => {
    const model = (fixPunkt as any)[modelKey];
    if (model == null) {
      console.log("not found", { modelKey, key });
      return;
    }
    const box = model.getBox();
    const corners = [
      v3(box.min.x, 0, box.min.z),
      v3(box.max.x, 0, box.min.z),
      v3(box.max.x, 0, box.max.z),
      v3(box.min.x, 0, box.max.z),
    ]
      .map((v) => model.localToWorld(v))
      .map(cs.pointToScreen);
    const points = corners.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    return (
      <polygon key={key} points={points} className={className} style={{ fill: "currentColor" }} />
    );
  };

  return (
    <g>
      <line
        {...punkteZuLinie([balkenStartScreen, balkenEndScreen])}
        strokeWidth={balkenStrokeWidth}
        className={className}
      />
      <line
        {...punkteZuLinie([balkenEndScreen, balkenEndDreieckScreen])}
        strokeWidth={balkenStrokeWidth}
        className={className + " schiebetor-dreieck"}
      />
      <line
        {...punkteZuLinie([balkenEndDreieckScreen, balkenEndOffenScreen])}
        strokeWidth={balkenStrokeWidth}
        className={className + " schiebetor-oeffnungsbereich"}
      />
      {renderModelBox("Schiebetor_Rollenbock1", "rollenbock1")}
      {renderModelBox("Schiebetor_Rollenbock2", "rollenbock2")}
      {renderModelBox("Schiebetor_Entlastungsrolle", "entlastungsrolle")}
      {renderModelBox("Schiebetor_EAntrieb_Unterbau", "motor-unterbau")}
    </g>
  );
}

export interface GFlügel {
  xs: number[];
  isAufhängungAnLinkerTorseite: boolean;
  isÖffnungInSegmentNormale: boolean;
  localToWorld: (v3: Vector3) => Vector3;
}

export function TorFlügel({
  cs,
  gflügel,
  className,
}: {
  cs: CoordinateSystem;
  gflügel: GFlügel[];
  className: string;
}) {
  const öffnungswinkel = 90;
  return (
    <>
      {gflügel.map(
        ({ xs, isAufhängungAnLinkerTorseite, isÖffnungInSegmentNormale, localToWorld }, index) => {
          const points = xs.map((x) => v3(x, 0, 0));
          const [a, b] = isAufhängungAnLinkerTorseite ? points : [points[1], points[0]];
          const m_ = new Matrix4();
          const translate = (m: Matrix4, v: Vector3) =>
            m.multiply(m_.makeTranslation(v.x, v.y, v.z));
          const rotateY = (m: Matrix4, angle: number) => m.multiply(m_.makeRotationY(angle));
          const richtung = isÖffnungInSegmentNormale ? -1 : +1;
          const m = translate(
            rotateY(translate(new Matrix4(), a), (richtung * öffnungswinkel * Math.PI) / 180),
            a.clone().negate()
          );
          const b_ = b.clone().applyMatrix4(m);
          const [ap, bp, b_p] = [a, b, b_].map((p) => localToWorld(p)).map(cs.pointToScreen);
          const radiusPx = cs.lengthToScreen(xs[1] - xs[0]);
          const largeArc = false ? "1" : "0";
          const clockwiseArc = isÖffnungInSegmentNormale ? "1" : "0";

          const pathD = `M ${bp.x} ${bp.y} A ${radiusPx} ${radiusPx} 0 ${largeArc} ${clockwiseArc} ${b_p.x} ${b_p.y}`;
          const strokeWidth = cs.lengthToScreen(0.04);
          return (
            <g key={index}>
              <path d={pathD} className={className} />
              <line
                {...punkteZuLinie([ap, b_p])}
                strokeWidth={strokeWidth}
                className={className}
              ></line>
              ;
            </g>
          );
        }
      )}
    </>
  );
}
